import { isNil } from 'lodash-es';
import sum from 'lodash-es/sum';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { useClient } from '../ClientProvider';
import { formatHistoryDataForCharts, getChartDomains } from '../core/charts';
import { denormalizeDataPoint, normalizeDataPoint } from '../core/charts/charts';
import { useArvalisIds } from '../core/dss/waterBalance';
import { SURFACE_POWER_THRESHOLD } from '../core/history/history';
import { DataSources } from '../enums';
import TimeSteps from '../enums/TimeSteps';
import { AggregationType } from '../resources/measurements.type';
import { EnvironmentType } from '../resources/plots';
import useQuery from '../useQuery';
import useConvertedValue from './useConvertedValue';
function sumByMonth({ historyValues, startOfMonth, value }) {
    const monthPoint = historyValues?.find((point) => point.timestamp == startOfMonth.unix());
    if (!isNil(monthPoint)) {
        monthPoint.value = (monthPoint.value ?? 0) + value;
    }
    else {
        historyValues?.push({
            value,
            timestamp: startOfMonth.unix(),
            source: DataSources.measurement
        });
    }
}
const EMPTY_FORMATTED_DATA_POINT = [];
const getIrrigationWeatherConditionsChart = ({ metric, historyData, timeStep, convertValue, convertNumberValue, getUnit }) => {
    const data = historyData
        ? formatHistoryDataForCharts({
            data: historyData,
            aggregateToExtract: AggregationType.sum,
            metric,
            conversionFct: (value) => convertNumberValue({ metric, value })
        })
        : EMPTY_FORMATTED_DATA_POINT;
    const { yDomain, absoluteYDomain, xDomain } = getChartDomains({
        metric,
        timeStep,
        unit: getUnit(metric),
        data
    });
    const normalizeScale = normalizeDataPoint(yDomain);
    const normalizedData = data.map((point) => ({
        ...point,
        y: normalizeScale(point.value)
    }));
    const denormalizeFct = denormalizeDataPoint(yDomain);
    const normalizeFct = normalizeDataPoint(yDomain);
    const next7DaysValues = data.slice(8, undefined).map((dp) => dp.value);
    const previous7DaysValues = data.slice(undefined, 7).map((dp) => dp.value);
    const allValues = data.map((d) => d.value);
    const rawNext7DaysSum = Math.round((sum(next7DaysValues) + Number.EPSILON) * 100) / 100;
    const rawPrevious7DaysSum = Math.round((sum(previous7DaysValues) + Number.EPSILON) * 100) / 100;
    const rawTotal = Math.round((sum(allValues) + Number.EPSILON) * 100) / 100;
    const next7DaysSum = convertValue({ metric, value: rawNext7DaysSum });
    const previous7DaysSum = convertValue({
        metric,
        value: rawPrevious7DaysSum
    });
    const total = convertValue({
        metric,
        value: rawTotal
    });
    const todaysInsolation = convertValue({
        metric: 'COLD_T_HOUR',
        value: data.reduce((acc, d) => {
            if ((d.value ?? 0) > SURFACE_POWER_THRESHOLD) {
                return ++acc;
            }
            return acc;
        }, 0)
    });
    return {
        data: normalizedData,
        denormalizeFct,
        normalizeFct,
        absoluteYDomain,
        yDomain,
        xDomain,
        next7DaysSum,
        previous7DaysSum,
        total,
        todaysInsolation
    };
};
const useIrrigationWeatherConditions = ({ plot }) => {
    const client = useClient();
    const { convertNumberValue, convertValue, getUnit } = useConvertedValue();
    const isGreenHouse = plot.meta?.environmentType === EnvironmentType.greenHouse;
    const today = moment().tz(plot.timezone).startOf('day');
    const pastWeekAndNextWeek = {
        span: TimeSteps.perDays,
        since: today.clone().subtract(7, 'day').startOf('day').unix(),
        before: today.clone().add(8, 'day').startOf('day').unix()
    };
    const todayPerHour = {
        span: TimeSteps.perHours,
        since: today.clone().tz(plot.timezone).startOf('day').unix(),
        before: today.clone().tz(plot.timezone).add(1, 'day').startOf('day').unix()
    };
    const openFieldHistoryRequest = useQuery(client.measurements.plots.histories.get1_1({
        plotId: plot.id,
        ...pastWeekAndNextWeek,
        metrics: ['RR', 'etp']
    }), { enabled: !isGreenHouse });
    const greenHouseHistoryRequest = useQuery(client.measurements.plots.histories.get1_1({
        plotId: plot.id,
        ...todayPerHour,
        metrics: ['SSI']
    }), { enabled: isGreenHouse });
    const historyData = isGreenHouse ? greenHouseHistoryRequest.data : openFieldHistoryRequest.data;
    const vpdRequest = useQuery(client.vpd.last({
        plotId: plot.id
    }), { enabled: isGreenHouse });
    const vpdData = vpdRequest.data;
    const etpChart = getIrrigationWeatherConditionsChart({
        metric: 'etp',
        historyData,
        timeStep: TimeSteps.perDays,
        convertNumberValue,
        convertValue,
        getUnit
    });
    const rainfallsChart = getIrrigationWeatherConditionsChart({
        metric: 'RR',
        historyData,
        timeStep: TimeSteps.perDays,
        convertNumberValue,
        convertValue,
        getUnit
    });
    const ssiChart = getIrrigationWeatherConditionsChart({
        metric: 'SSI',
        historyData,
        timeStep: TimeSteps.perHours,
        convertNumberValue,
        convertValue,
        getUnit
    });
    return {
        etpChart,
        historyRequest: isGreenHouse ? greenHouseHistoryRequest : openFieldHistoryRequest,
        period: pastWeekAndNextWeek,
        rainfallsChart,
        ssiChart,
        vpdData,
        vpdRequest
    };
};
export const useArvalisIrrigationWeatherConditions = ({ plot, isOver, isEnabled }) => {
    const { arvalisPlotId, clientId } = useArvalisIds(plot);
    const client = useClient();
    const today = moment().tz(plot.timezone).startOf('day');
    const historyRequest = useQuery(client.waterBalanceArvalis.getResult({
        plotId: arvalisPlotId,
        clientId: clientId,
        start: !isOver ? today.clone().subtract(7, 'day').startOf('day').toDate() : undefined,
        end: !isOver ? today.clone().add(8, 'day').startOf('day').toDate() : undefined
    }), { enabled: !isNil(arvalisPlotId) && !isNil(clientId) && isEnabled });
    const period = useMemo(() => {
        //keep today inside useMemo because if it's outside it should be a dep but if it's a dep it change at each render
        const privateToday = moment().tz(plot.timezone).startOf('day');
        return !isOver
            ? {
                span: TimeSteps.perDays,
                since: privateToday.clone().subtract(7, 'day').startOf('day').unix(),
                before: privateToday.clone().add(8, 'day').startOf('day').unix()
            }
            : {
                span: TimeSteps.perMonth,
                since: moment(historyRequest.data?.waterBalance.at(0)?.date ?? privateToday).unix(),
                before: moment(historyRequest.data?.waterBalance.at(-1)?.date ?? privateToday).unix()
            };
    }, [historyRequest.data?.waterBalance, isOver, plot.timezone]);
    const historyData = useMemo(() => {
        const initialData = {
            values: { RR: { avg: [] }, etp: { avg: [] }, DRN: { avg: [] } },
            ...period
        };
        if (isNil(historyRequest.data)) {
            return initialData;
        }
        return !isOver
            ? historyRequest.data.waterBalance.reduce((acc, waterBalance) => {
                if (!isNil(waterBalance.rainfalls)) {
                    acc.values.RR?.avg?.push({
                        value: waterBalance.rainfalls,
                        timestamp: moment(waterBalance.date).unix(),
                        source: DataSources.measurement
                    });
                }
                if (!isNil(waterBalance.etp)) {
                    acc.values.etp?.avg?.push({
                        value: waterBalance.etp,
                        timestamp: moment(waterBalance.date).unix(),
                        source: DataSources.measurement
                    });
                }
                if (!isNil(waterBalance.profileDrainage)) {
                    acc.values.DRN?.avg?.push({
                        value: waterBalance.profileDrainage,
                        timestamp: moment(waterBalance.date).unix(),
                        source: DataSources.measurement
                    });
                }
                return acc;
            }, initialData)
            : historyRequest.data.waterBalance.reduce((acc, waterBalance) => {
                const startOfMonth = moment.tz(waterBalance.date, plot.timezone).startOf('month');
                if (!isNil(waterBalance.rainfalls)) {
                    sumByMonth({
                        historyValues: acc?.values?.RR?.avg,
                        startOfMonth,
                        value: waterBalance.rainfalls
                    });
                }
                if (!isNil(waterBalance.etp)) {
                    sumByMonth({
                        historyValues: acc.values.etp?.avg,
                        startOfMonth,
                        value: waterBalance.etp
                    });
                }
                if (!isNil(waterBalance.profileDrainage)) {
                    sumByMonth({
                        historyValues: acc.values.DRN?.avg,
                        startOfMonth,
                        value: waterBalance.profileDrainage
                    });
                }
                return acc;
            }, initialData);
    }, [historyRequest.data, isOver, period, plot.timezone]);
    const timeStep = isOver ? TimeSteps.perDays : TimeSteps.perDays;
    const { convertNumberValue, convertValue, getUnit } = useConvertedValue();
    const charts = useMemo(() => {
        const etpChart = getIrrigationWeatherConditionsChart({
            metric: 'etp',
            historyData,
            timeStep,
            convertNumberValue,
            convertValue,
            getUnit
        });
        const rainfallsChart = getIrrigationWeatherConditionsChart({
            metric: 'RR',
            historyData,
            timeStep,
            convertNumberValue,
            convertValue,
            getUnit
        });
        const drainageChart = getIrrigationWeatherConditionsChart({
            metric: 'DRN',
            historyData,
            timeStep,
            convertNumberValue,
            convertValue,
            getUnit
        });
        return { drainageChart, rainfallsChart, etpChart };
    }, [convertNumberValue, convertValue, getUnit, historyData, timeStep]);
    return {
        historyRequest,
        ...charts,
        period
    };
};
export default useIrrigationWeatherConditions;
